import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * ProtectedRoute
 *
 * @param {Array<string>} requiredPermissions - Array of permission strings required to access this route
 * @param {Array<string>} allowedRoles - Array of roles allowed to access this route
 */
function ProtectedRoute({ requiredPermissions = [], allowedRoles = [] }) {
  const { isAuthenticated, loading, userRole, permissions } = useAuth();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <p>Checking authentication status...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.warn('🚫 User not authenticated. Redirecting to login.');
    return <Navigate to="/login" replace />;
  }

  // ✅ Normalize roles for case-insensitive comparison
  const normalizedUserRole = userRole?.toLowerCase();
  const normalizedAllowedRoles = allowedRoles.map((role) => role.toLowerCase());

  // ✅ 🔒 Super Admin bypass: always allow full access
  if (normalizedUserRole === 'super admin' || normalizedUserRole === 'superadmin') {
    console.log('🟢 Super Admin detected — full access granted.');
    return <Outlet />;
  }

  // Check allowed roles
  if (
    allowedRoles.length > 0 &&
    !normalizedAllowedRoles.includes(normalizedUserRole)
  ) {
    console.warn(
      `🚫 Role "${userRole}" not authorized. Allowed roles:`,
      allowedRoles
    );
    return <Navigate to="/unauthorized" replace />;
  }

  // Check permissions
  if (
    requiredPermissions.length > 0 &&
    !requiredPermissions.every((perm) => permissions.includes(perm))
  ) {
    console.warn(
      `🚫 Missing permissions. Required: ${requiredPermissions}, user has: ${permissions}`
    );
    return <Navigate to="/unauthorized" replace />;
  }

  // ✅ Passed all checks
  console.log('✅ Access granted to protected route.');
  return <Outlet />;
}

export default ProtectedRoute;
